import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  TextField,
  Grid,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  Stack,
  Box,
  Paper,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import SnackbarInfo from '../common/snackbar-info.component';
import { updateDataById } from '../../services/apps.service';
import IapSettings from '../form-elements/iap-settings.component';
import { IAP_SETTINGS } from '../../config/const.config';
import validationRules, {
  maxLengthValidation,
} from '../../utils/validations.util';

const AppSubscriptionSettings = ({
  channelDetail,
  type,
  handleRefreshData,
  formValueChangedCallback,
}) => {
  const samsungIAPValue = channelDetail?.samsungTvIapSetting
    ? JSON.parse(channelDetail.samsungTvIapSetting)
    : null;
  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
    setValue,
  } = useForm({
    defaultValues: {
      monthlySubscriptionPrice: channelDetail.monthlySvodPrice.toString() || '',
      yearlySubscriptionPrice: channelDetail.yearlySvodPrice.toString() || '',
      svodButtonText: channelDetail.svodButtonText || '',
      svodStartPrice: channelDetail.svodStartPrice || '',
      tvodStartPrice: channelDetail.tvodStartPrice || '',
      tvodExpire: channelDetail.tvodExpire.toString() || null,
      hideMonthlyPlan: !!(channelDetail.hideMonthlyPlan || 0),
      iapRoku: channelDetail.rokuIapSetting || 'none',
      iapWeb: channelDetail.webIapSetting || 'none',
      iapFireTvMobile: channelDetail.firetvIapSetting || 'none',
      iapAndroidTvMobile: channelDetail.androidIapSetting || 'none',
      iapAppleTvMobile: channelDetail.appleIapSetting || 'none',
      iapSamsungTv: samsungIAPValue?.appId ? 'SAMSUNG_YES' : 'none',
      iapLgTv: channelDetail.lgTvIapSetting || 'none',
      legacySVODProducts: channelDetail.legacySvodPackages || '',
      samsungStoreAppId: samsungIAPValue?.appId || '',
      samsungStoreSecretKey: samsungIAPValue?.secretKey || '',
    },
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showSamsungIAPSetting, setShowSamsungIAPSetting] = useState(
    samsungIAPValue?.appId || false
  );
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const payload = {
      ...data,
      type,
    };
    updateDataById(channelDetail.id, payload)
      .then(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: 'Monetization Settings updated successfully.',
        });
        formValueChangedCallback(false);
        handleRefreshData();
        setFormSubmitted(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  useEffect(() => {
    if (Object.keys(dirtyFields).length > 0) {
      formValueChangedCallback(true);
    } else {
      formValueChangedCallback(false);
    }
  }, [Object.keys(dirtyFields).length]);

  return (
    <Box component={Paper} p={2}>
      <form
        id="subscription-settings-form"
        onSubmit={handleSubmit(onFormSubmit)}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12}>
            <Divider
              textAlign="center"
              variant="fullWidth"
              sx={{ fontWeight: 'bold' }}
            >
              Subscription Prices
            </Divider>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Box color="red" fontSize="small">
              These prices only update the text on the app splash screens and
              website. Admin must manually update subscription IAP plans for web
              and on all CTV platforms.
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="monthlySubscriptionPrice"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Monthly Subscription Price"
                  type="number"
                  error={!!errors.monthlySubscriptionPrice}
                  helperText={errors?.monthlySubscriptionPrice?.message || null}
                  fullWidth
                  variant="filled"
                  onBlur={(e) =>
                    setValue('svodStartPrice', `$${e.target.value}/month`)
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="yearlySubscriptionPrice"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Yearly Subscription Price"
                  type="number"
                  error={!!errors.yearlySubscriptionPrice}
                  helperText={errors?.yearlySubscriptionPrice?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Divider
              textAlign="center"
              variant="fullWidth"
              sx={{ fontWeight: 'bold', mt: 3 }}
            >
              App Splash Screen Text
            </Divider>
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="svodButtonText"
              control={control}
              rules={maxLengthValidation(64)}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="SVOD Button Text"
                  type="text"
                  error={!!errors.svodButtonText}
                  helperText={errors?.svodButtonText?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="svodStartPrice"
              control={control}
              rules={maxLengthValidation(64)}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="SVOD Start Price"
                  type="text"
                  error={!!errors.svodStartPrice}
                  helperText={errors?.svodStartPrice?.message || null}
                  fullWidth
                  variant="filled"
                  disabled
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="tvodStartPrice"
              control={control}
              rules={maxLengthValidation(64)}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="TVOD Start Price"
                  type="text"
                  error={!!errors.tvodStartPrice}
                  helperText={errors?.tvodStartPrice?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="tvodExpire"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="TVOD Expiration"
                  type="number"
                  error={!!errors.tvodExpire}
                  helperText={errors?.tvodExpire?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Divider
              textAlign="center"
              variant="fullWidth"
              sx={{ fontWeight: 'bold', mt: 3 }}
            >
              Other Options
            </Divider>
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="hideMonthlyPlan"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={value}
                      onChange={onChange}
                      inputProps={{ 'aria-label': 'controlled' }}
                      color="primary"
                    />
                  }
                  label="Hide Monthly Plan"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} />

          <Grid item xs={12} sm={12} md={12}>
            <Divider
              textAlign="center"
              variant="fullWidth"
              sx={{ fontWeight: 'bold', mt: 3 }}
            >
              App Developer Accounts (for Payment Validation)
            </Divider>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Box color="red" fontSize="small">
              WARNING: Changing these settings after the app is live will break
              payment validation flow.
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="iapRoku"
              control={control}
              rules={validationRules.REQUIRED}
              render={({ field: { onChange, value } }) => (
                <IapSettings
                  id="iap_roku"
                  name="iap_roku"
                  label="Roku"
                  defaultValue={value}
                  onChange={onChange}
                  validOptions={Object.keys(IAP_SETTINGS).filter((item) =>
                    item.includes('ROKU')
                  )}
                  error={errors?.iapRoku?.message || ''}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="iapWeb"
              control={control}
              rules={validationRules.REQUIRED}
              render={({ field: { onChange, value } }) => (
                <IapSettings
                  id="iap_web"
                  name="iap_web"
                  label="Web"
                  defaultValue={value}
                  onChange={onChange}
                  validOptions={Object.keys(IAP_SETTINGS).filter((item) =>
                    item.includes('WEB')
                  )}
                  error={errors?.iapWeb?.message || ''}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="iapFireTvMobile"
              control={control}
              rules={validationRules.REQUIRED}
              render={({ field: { onChange, value } }) => (
                <IapSettings
                  id="iap_fire_tv_mobile"
                  name="iap_fire_tv_mobile"
                  label="Fire TV / Mobile"
                  defaultValue={value}
                  onChange={onChange}
                  validOptions={Object.keys(IAP_SETTINGS).filter((item) =>
                    item.includes('FIRETV')
                  )}
                  error={errors?.iapFireTvMobile?.message || ''}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="iapAndroidTvMobile"
              control={control}
              rules={validationRules.REQUIRED}
              render={({ field: { onChange, value } }) => (
                <IapSettings
                  id="iap_android_tv_mobile"
                  name="iap_android_tv_mobile"
                  label="Android TV / Mobile"
                  defaultValue={value}
                  onChange={onChange}
                  validOptions={Object.keys(IAP_SETTINGS).filter((item) =>
                    item.includes('ANDROID')
                  )}
                  error={errors?.iapAndroidTvMobile?.message || ''}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="iapAppleTvMobile"
              control={control}
              rules={validationRules.REQUIRED}
              render={({ field: { onChange, value } }) => (
                <IapSettings
                  id="iap_apple_tv_mobile"
                  name="iap_apple_tv_mobile"
                  label="Apple TV / Mobile"
                  defaultValue={value}
                  onChange={onChange}
                  validOptions={Object.keys(IAP_SETTINGS).filter((item) =>
                    item.includes('APPLE')
                  )}
                  error={errors?.iapAppleTvMobile?.message || ''}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="iapSamsungTv"
              control={control}
              rules={validationRules.REQUIRED}
              render={({ field: { onChange, value } }) => (
                <IapSettings
                  id="iap_samsung_tv"
                  name="iap_samsung_tv"
                  label="Samsung TV"
                  defaultValue={value}
                  onChange={(e) => {
                    setShowSamsungIAPSetting(
                      e?.target?.value === 'SAMSUNG_YES' || false
                    );
                    onChange(e);
                  }}
                  validOptions={Object.keys(IAP_SETTINGS).filter((item) =>
                    item.includes('SAMSUNG')
                  )}
                  error={errors?.iapSamsungTv?.message || ''}
                />
              )}
            />
          </Grid>
          {showSamsungIAPSetting && (
            <>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="samsungStoreAppId"
                  control={control}
                  rules={validationRules.REQUIRED}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Samsung Store APP ID *"
                      type="text"
                      error={!!errors.name}
                      helperText={errors?.name?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="samsungStoreSecretKey"
                  control={control}
                  rules={validationRules.REQUIRED}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Samsung Store Secret Key *"
                      type="text"
                      error={!!errors.name}
                      helperText={errors?.name?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="iapLgTv"
              control={control}
              rules={validationRules.REQUIRED}
              render={({ field: { onChange, value } }) => (
                <IapSettings
                  id="iap_lg_tv"
                  name="iap_lg_tv"
                  label="LG TV"
                  defaultValue={value}
                  onChange={onChange}
                  validOptions={Object.keys(IAP_SETTINGS).filter((item) =>
                    item.includes('LG')
                  )}
                  error={errors?.iapLgTv?.message || ''}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} />

          <Grid item xs={12} sm={12} md={12}>
            <Divider
              textAlign="center"
              variant="fullWidth"
              sx={{ fontWeight: 'bold', mt: 3 }}
            >
              Legacy SVOD Products (Comma Separated)
            </Divider>
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Box color="red" fontSize="small">
              SVOD Products for legacy active subscriptions created from older
              app hosting companies (pre-CMS)
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="legacySVODProducts"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Legacy SVOD Products (Comma Seperated)"
                  type="text"
                  multiline
                  minRows={5}
                  maxRows={5}
                  autoComplete="off"
                  error={!!errors.legacySVODProducts}
                  helperText={errors?.legacySVODProducts?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} />
        </Grid>
      </form>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ py: 2.5 }}
      >
        <Button
          type="submit"
          form="subscription-settings-form"
          color="primary"
          variant="contained"
          disabled={formSubmitted}
        >
          Save Monetization Settings
        </Button>
        <Typography variant="body2" color="secondary">
          * indicates a required field
        </Typography>
      </Stack>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Box>
  );
};

AppSubscriptionSettings.propTypes = {
  channelDetail: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  handleRefreshData: PropTypes.func.isRequired,
  formValueChangedCallback: PropTypes.func.isRequired,
};

export default AppSubscriptionSettings;
